// What a person did on the TimeBlocker screen, so ⌘Z can put it back.
//
// Promise:
// - Placing, moving, resizing, retitling and removing a Block, and adding or
//   removing a link, are recorded once the server accepted them. Starting or
//   ending a Block is not (an ended Block cannot be started again) and makes
//   the history forget that Block.
// - Undo puts the Block back as it was (an Action the change planned goes back
//   to its plan); redo does it again. A new change clears what could be redone.
// - A removed Block comes back as a new Block (same time, title and links; the
//   running state is not restored). Entries that name the old Block follow it.
// - Undo waits for the writes still on their way, so it takes back what is on
//   the screen, not the change before it. It does not check what others
//   changed meanwhile: it puts the earlier values back.
// - An entry the server answers "gone" or "changed" (404, 409) is dropped and
//   the error is passed on; after any other failure the entry stays to try again.
// - The history belongs to one QueryClient (one signed-in session) and is kept
//   in memory only.
import type { QueryClient } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";
import { ApiError } from "../data/api";

export interface HistoryEntry {
  /** The Blocks it is about (as they were named when it was recorded). */
  blocks: string[];
  undo(): Promise<void>;
  redo(): Promise<void>;
}

export interface HistoryState {
  canUndo: boolean;
  canRedo: boolean;
}

const LIMIT = 50;

export class BlockHistory {
  private undos: HistoryEntry[] = [];
  private redos: HistoryEntry[] = [];
  private running = false;
  /** Counts the changes pushed, to see whether one came in during an undo or redo. */
  private pushed = 0;
  private inflight = 0;
  private waiting: (() => void)[] = [];
  /** A removed Block's id → the id of the Block that took its place. */
  private moved = new Map<string, string>();
  private subs = new Set<() => void>();
  private state: HistoryState = { canUndo: false, canRedo: false };

  subscribe = (fn: () => void) => {
    this.subs.add(fn);
    return () => this.subs.delete(fn);
  };
  getState = () => this.state;

  /** The Block an id stands for now (a re-created Block has a new id). */
  resolve(id: string): string {
    let now = id;
    for (let hops = 0; this.moved.has(now) && hops < 1000; hops++)
      now = this.moved.get(now) as string;
    return now;
  }
  replaced(oldId: string, newId: string) {
    const from = this.resolve(oldId);
    if (from !== newId) this.moved.set(from, newId);
  }

  /** A recorded write has started / finished (an undo waits until none is on its way). */
  begin() {
    this.inflight++;
  }
  end() {
    if (--this.inflight > 0) return;
    for (const wake of this.waiting.splice(0)) wake();
  }
  private idle(): Promise<void> {
    return this.inflight === 0
      ? Promise.resolve()
      : new Promise((resolve) => this.waiting.push(resolve));
  }

  /** Records something just done by the person. */
  push(entry: HistoryEntry) {
    this.undos.push(entry);
    if (this.undos.length > LIMIT) this.undos.shift();
    this.redos = [];
    this.pushed++;
    this.changed();
  }

  /** Forgets what is recorded about a Block (it can no longer be taken back). */
  forget(id: string) {
    const gone = this.resolve(id);
    const keep = (e: HistoryEntry) =>
      !e.blocks.some((b) => this.resolve(b) === gone);
    this.undos = this.undos.filter(keep);
    this.redos = this.redos.filter(keep);
    this.changed();
  }

  undo = () => this.step("undo");
  redo = () => this.step("redo");

  private async step(way: "undo" | "redo") {
    // One at a time: the next one may name what this one is still writing.
    if (this.running) return;
    this.running = true;
    this.changed();
    try {
      await this.idle();
      const from = way === "undo" ? this.undos : this.redos;
      const entry = from.pop();
      if (!entry) return;
      const mark = this.pushed;
      const below = this.undos.length;
      try {
        await entry[way]();
      } catch (e) {
        const gone =
          e instanceof ApiError && (e.status === 404 || e.status === 409);
        // After another kind of failure it can be tried again (unless a new
        // change has already cleared the redo list).
        if (!gone && (way === "undo" || mark === this.pushed)) from.push(entry);
        throw e;
      }
      if (way === "undo") {
        // A change made meanwhile is the newest: nothing is left to redo.
        if (mark === this.pushed) this.redos.push(entry);
      } else {
        // The redone entry goes under the changes made meanwhile.
        this.undos.splice(Math.min(below, this.undos.length), 0, entry);
      }
    } finally {
      this.running = false;
      this.changed();
    }
  }

  private changed() {
    const canUndo = !this.running && this.undos.length > 0;
    const canRedo = !this.running && this.redos.length > 0;
    if (canUndo === this.state.canUndo && canRedo === this.state.canRedo)
      return;
    this.state = { canUndo, canRedo };
    for (const fn of this.subs) fn();
  }
}

const histories = new WeakMap<QueryClient, BlockHistory>();

export function historyOf(qc: QueryClient): BlockHistory {
  let h = histories.get(qc);
  if (!h) {
    h = new BlockHistory();
    histories.set(qc, h);
  }
  return h;
}

export function useBlockHistory(): BlockHistory {
  return historyOf(useQueryClient());
}

/** Whether there is something to undo and to redo (redraws when that changes). */
export function useHistoryState(): HistoryState {
  const h = useBlockHistory();
  return useSyncExternalStore(h.subscribe, h.getState, h.getState);
}
